/* eslint-disable react-hooks/exhaustive-deps */
import { EditOutlined, DeleteOutlined, CheckCircleOutlined, StopOutlined } from "@ant-design/icons";
import { Table, Button, Space, Tag, Tooltip, Popconfirm, Typography, theme, Grid } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import CreateEditJobModal from "./CreateEditJobModal";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";
import { clamp } from "../../utils/responsiveHelpers";

const { Text } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

/**
 * ADMIN-ONLY derived status logic
 */
const getJobStatus = (job) => {
  if (!job.isActive) {
    return { label: "DRAFT", color: "default" };
  }
  const now = dayjs();
  if (job.activeFrom && now.isBefore(dayjs(job.activeFrom))) {
    return { label: "SCHEDULED", color: "blue" };
  }
  if (job.activeTill && now.isAfter(dayjs(job.activeTill))) {
    return { label: "EXPIRED", color: "red" };
  }
  return { label: "ACTIVE", color: "green" };
};

const CareersManagementDash = ({ userConfig, ROOT_URL }) => {
  const hasManagementAccess = userConfig?.careers?.hasManagementAccess || false;
  const { token } = useToken();
  const screens = useBreakpoint();
  const isMobileView = screens.md === false;

  const BASE_URL = "admin/careers";

  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Fetch jobs list
   */
  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${ROOT_URL}/${BASE_URL}/list`);
      const list = Array.isArray(res.data) ? res.data : [];
      setJobsData(
        list.map((job, idx) => ({
          ...job,
          key: job.jobId,
          index: idx + 1
        }))
      );
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create / Update Job
   */
  const handleSaveJob = async (formValues) => {
    const isEdit = !!initialValues;
    const endpoint = isEdit ? `${BASE_URL}/job/${initialValues.jobId}` : `${BASE_URL}/job`;

    try {
      if (isEdit) {
        await axios.patch(`${ROOT_URL}/${endpoint}`, formValues);
      } else {
        await axios.post(`${ROOT_URL}/${endpoint}`, formValues);
      }

      showSuccessNotification(`Job ${isEdit ? "updated" : "created"} successfully`);
      setIsModalOpen(false);
      setInitialValues(null);
      fetchJobs();
    } catch (err) {
      showErrorNotification(err);
    }
  };

  /**
   * Toggle job active status
   */
  const toggleJobStatus = async (jobId, isActive) => {
    try {
      await axios.post(`${ROOT_URL}/${BASE_URL}/job/${jobId}/toggle-activation`);

      showSuccessNotification(`Job ${isActive ? "deactivated" : "activated"} successfully`);

      fetchJobs();
    } catch (err) {
      showErrorNotification(err);
    }
  };

  /**
   * Delete Job
   */
  const deleteJob = async (jobId) => {
    try {
      await axios.delete(`${ROOT_URL}/${BASE_URL}/job/${jobId}`);
      showSuccessNotification("Job deleted successfully");
      fetchJobs();
    } catch (err) {
      showErrorNotification(err);
    }
  };

  const getPublicJobUrl = (jobId) => `${window.location.origin}/careers/${jobId}`;

  /**
   * Table columns
   */
  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      width: 70
    },
    {
      title: "Title",
      dataIndex: "title",
      width: 180
    },
    {
      title: "Description",
      dataIndex: "description",
      width: 320
    },
    {
      title: "Location",
      dataIndex: "location",
      width: 120
    },
    {
      title: "Employment",
      dataIndex: "employmentType",
      width: 100,
      render: (t) => <Tag>{t}</Tag>
    },
    {
      title: "Salary Range",
      dataIndex: "salaryRangeDescription",
      width: 180
    },
    {
      title: "Public URL",
      dataIndex: "jobId",
      width: 260,
      render: (_, job) => {
        const url = getPublicJobUrl(job.jobId);
        return (
          <span
            style={{ overflowWrap: "anywhere", wordBreak: "break-word", whiteSpace: "normal" }}
          >
            {url}
          </span>
        );
      }
    },
    {
      title: "Form Link",
      dataIndex: "applyRedirectUrl",
      width: 230
    },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 120,
      render: (_, job) => {
        const status = getJobStatus(job);
        return <Tag color={status.color}>{status.label}</Tag>;
      }
    },
    {
      title: "Actions",
      fixed: isMobileView ? undefined : "right",
      width: 120,
      render: (row) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              disabled={!hasManagementAccess}
              onClick={() => {
                setInitialValues(row);
                setIsModalOpen(true);
              }}
            />
          </Tooltip>

          <Tooltip title={row.isActive ? "Deactivate" : "Activate"}>
            <Button
              type="text"
              icon={row.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
              disabled={!hasManagementAccess}
              onClick={() => toggleJobStatus(row.jobId, row.isActive)}
            />
          </Tooltip>

          <Popconfirm
            title="Delete this job?"
            onConfirm={() => deleteJob(row.jobId)}
            disabled={!hasManagementAccess}
            placement="left"
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} disabled={!hasManagementAccess} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchJobs();
  }, []);

  return (
    <div style={{ padding: clamp(12, 24) }}>
      <div
        style={{
          background: token.colorBgContainer,
          padding: 20,
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: "12px"
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Careers</h2>
            <Text type="secondary">Manage job listings shown on the website</Text>
          </div>

          {hasManagementAccess && (
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setInitialValues(null);
                setIsModalOpen(true);
              }}
            >
              + New Job
            </Button>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={jobsData}
          loading={loading}
          bordered
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
        />
      </div>

      {isModalOpen && (
        <CreateEditJobModal
          initialValues={initialValues}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onSubmit={handleSaveJob}
        />
      )}
    </div>
  );
};

export default CareersManagementDash;
